/**
 * "Add calculated row" — define a metric the grid computes from the ones
 * already in it.
 *
 * The expression is free text on purpose: an expression builder would have to
 * enumerate an arithmetic the server owns, and the server is the thing that
 * decides whether a formula is usable. So the dialog owns the half a client
 * can get right — the NAMES — and leaves the arithmetic to the 422, which says
 * exactly what is wrong with a formula and which no client-side guess improves
 * on.
 *
 * The names are offered the way the chat offers documents: type a word, pick
 * from a dropdown, and the metric's KEY is inserted. Printing the vocabulary in
 * a paragraph under the box (the first version of this) left the user to
 * transcribe `total_units` from a wall of text — "total units / occupied
 * units" reads exactly like the labels on screen and is rejected by the
 * server.
 *
 * A rejected submit leaves everything typed in place: the text that was almost
 * right is the best starting point for the next attempt.
 */

import { X } from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type {
	KpiCustomMetricSaveOutcome,
	KpiCustomMetricInput,
} from "../../services/kpiCustomMetricsService";
import type { KpiCustomMetric, KpiMetricGroup, KpiUnit } from "../../types/kpi";
import KpiMetricSuggestDropdown from "./KpiMetricSuggestDropdown";
import {
	type KpiMetricOption,
	useKpiMetricSuggest,
} from "./useKpiMetricSuggest";

export interface KpiAddMetricDialogProps {
	/** The loaded table's groups — the vocabulary an expression may use. */
	groups: KpiMetricGroup[];
	/**
	 * The row being edited, or null/absent to define a new one.
	 *
	 * One dialog for both verbs: the fields, the vocabulary, the autocomplete
	 * and the 422 handling are identical, and a second component would be the
	 * same file with two words changed — which is how the two drift apart.
	 */
	metric?: KpiCustomMetric | null;
	onSubmit: (input: KpiCustomMetricInput) => Promise<KpiCustomMetricSaveOutcome>;
	onClose: () => void;
}

const UNITS: { value: KpiUnit; label: string }[] = [
	{ value: "usd", label: "Dollars" },
	{ value: "count", label: "Count" },
	{ value: "pct", label: "Percent" },
	{ value: "ratio", label: "Ratio" },
];

/**
 * A failure that is NOT a rejected expression is ambiguous: the request may
 * have committed before the response was lost. The page refetches on this
 * outcome, so the honest instruction is to look before pressing Add again —
 * "please try again" is how a definition gets created twice.
 */
const FAILED_MESSAGE_CREATE =
	"Could not confirm the row was saved. The grid has been reloaded — check whether it is there before adding it again.";
/**
 * An edit is idempotent — repeating it cannot make a second row — so the honest
 * instruction here is simply to try again, not to go and look first.
 */
const FAILED_MESSAGE_EDIT =
	"Could not confirm the change was saved. The grid has been reloaded — check it, then try again.";

const SUGGEST_LIST_ID = "kpi-metric-expr-suggestions";
const suggestOptionId = (index: number): string =>
	`${SUGGEST_LIST_ID}-option-${index}`;

const KpiAddMetricDialog: React.FC<KpiAddMetricDialogProps> = ({
	groups,
	metric = null,
	onSubmit,
	onClose,
}) => {
	const editing = metric !== null;
	// Seeded once: the dialog is mounted per open (the page renders it behind a
	// condition), so a prop change mid-edit is not a case that arises — and
	// syncing state to props would overwrite what the user is typing.
	const [label, setLabel] = useState(metric?.label ?? "");
	const [expr, setExpr] = useState(metric?.expr ?? "");
	const [unit, setUnit] = useState<KpiUnit>(metric?.unit ?? "usd");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const labelRef = useRef<HTMLInputElement | null>(null);
	const exprRef = useRef<HTMLInputElement | null>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	/** Caret position an insertion asked for, applied after the value lands. */
	const pendingCursor = useRef<number | null>(null);

	// Focus starts inside and comes back out. `aria-modal` promises the rest of
	// the page is unavailable; without a restore, closing the dialog drops focus
	// to the top of the document and a keyboard user has to walk back to the
	// button they just pressed.
	useEffect(() => {
		const opener = document.activeElement;
		labelRef.current?.focus();
		return () => {
			if (opener instanceof HTMLElement) opener.focus();
		};
	}, []);

	/**
	 * Escape closes; Tab stays inside.
	 *
	 * A dialog that says `aria-modal` and then lets Tab walk into the grid
	 * behind it is lying to exactly the users who depend on the claim — and the
	 * controls back there start their own mutations.
	 */
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
				return;
			}
			if (event.key !== "Tab") return;
			const dialog = dialogRef.current;
			if (!dialog) return;
			const focusable = Array.from(
				dialog.querySelectorAll<HTMLElement>(
					"button, input, select, textarea, a[href], [tabindex]:not([tabindex='-1'])",
				),
			).filter((element) => !element.hasAttribute("disabled"));
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;
			if (event.shiftKey && (active === first || !dialog.contains(active))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	// The keys an expression can name, carried with their labels because the key
	// is what the formula takes and the label is what the user recognises.
	const vocabulary = useMemo<KpiMetricOption[]>(
		() =>
			groups.flatMap((group) =>
				group.metrics.map((metric) => ({
					key: metric.key,
					label: metric.label,
				})),
			),
		[groups],
	);

	const suggest = useKpiMetricSuggest(vocabulary);

	// A controlled input loses the caret to the end of the value on every
	// re-render, so an insertion in the middle of an expression has to put it
	// back explicitly.
	useLayoutEffect(() => {
		const target = pendingCursor.current;
		if (target === null) return;
		pendingCursor.current = null;
		exprRef.current?.setSelectionRange(target, target);
	}, [expr]);

	const applyEdit = useCallback(
		(edit: { value: string; cursor: number }) => {
			setExpr(edit.value);
			pendingCursor.current = edit.cursor;
			exprRef.current?.focus();
		},
		[],
	);

	const handleExprChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setExpr(event.target.value);
			suggest.handleChange(
				event.target.value,
				event.target.selectionStart ?? event.target.value.length,
			);
		},
		[suggest],
	);

	const handleExprKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			const cursor = event.currentTarget.selectionStart ?? expr.length;
			const outcome = suggest.handleKeyDown(event, expr, cursor);
			if (outcome && outcome !== "handled") applyEdit(outcome);
		},
		[applyEdit, expr, suggest],
	);

	const handleSuggestSelect = useCallback(
		(option: KpiMetricOption) => {
			const cursor = exprRef.current?.selectionStart ?? expr.length;
			applyEdit(suggest.selectMetric(option, expr, cursor));
		},
		[applyEdit, expr, suggest],
	);

	const canSubmit = label.trim().length > 0 && expr.trim().length > 0 && !saving;

	const handleSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			if (!canSubmit) return;
			setSaving(true);
			setError(null);
			const outcome = await onSubmit({
				label: label.trim(),
				expr: expr.trim(),
				unit,
			});
			setSaving(false);
			if (outcome.kind === "saved") {
				onClose();
				return;
			}
			// Both remaining outcomes keep the dialog open — a rejected expression
			// because the text is worth editing, a failed request because closing
			// would discard it for a reason the user cannot act on.
			setError(
				outcome.kind === "invalid"
					? outcome.message
					: editing
						? FAILED_MESSAGE_EDIT
						: FAILED_MESSAGE_CREATE,
			);
		},
		[canSubmit, editing, expr, label, onClose, onSubmit, unit],
	);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="kpi-add-metric-title"
				className="w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-lg"
			>
				<div className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
					<h2
						id="kpi-add-metric-title"
						className="text-lg font-semibold text-text-base"
					>
						{editing ? "Edit calculated row" : "Add calculated row"}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded p-1 text-text-muted hover:bg-gray-100"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="px-4 py-3">
					<label
						htmlFor="kpi-metric-label"
						className="block text-sm font-normal text-text-muted"
					>
						Name
					</label>
					<input
						id="kpi-metric-label"
						ref={labelRef}
						value={label}
						onChange={(event) => setLabel(event.target.value)}
						placeholder="Operating margin"
						className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-base text-text-base"
					/>

					<label
						htmlFor="kpi-metric-expr"
						className="mt-3 block text-sm font-normal text-text-muted"
					>
						Expression
					</label>
					<div className="relative mt-1">
						<input
							id="kpi-metric-expr"
							ref={exprRef}
							value={expr}
							onChange={handleExprChange}
							onKeyDown={handleExprKeyDown}
							onBlur={suggest.dismiss}
							placeholder="net_operating_income / total_revenue"
							autoComplete="off"
							role="combobox"
							aria-expanded={suggest.isActive}
							aria-autocomplete="list"
							aria-controls={SUGGEST_LIST_ID}
							aria-activedescendant={
								suggest.isActive
									? suggestOptionId(suggest.highlightedIndex)
									: undefined
							}
							className="w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm text-text-base"
						/>
						<KpiMetricSuggestDropdown
							results={suggest.results}
							highlightedIndex={suggest.highlightedIndex}
							visible={suggest.isActive}
							listId={SUGGEST_LIST_ID}
							optionId={suggestOptionId}
							onSelect={handleSuggestSelect}
						/>
					</div>
					<p className="mt-1 text-xs text-text-muted">
						Type a metric name — pick from suggestions; combine with + - * / (
						)
					</p>

					<label
						htmlFor="kpi-metric-unit"
						className="mt-3 block text-sm font-normal text-text-muted"
					>
						Unit
					</label>
					<select
						id="kpi-metric-unit"
						value={unit}
						onChange={(event) => setUnit(event.target.value as KpiUnit)}
						className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-base text-text-base"
					>
						{UNITS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>

					{error && (
						<p role="alert" className="mt-3 text-sm text-red-600">
							{error}
						</p>
					)}

					<div className="mt-4 flex justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							className="rounded border border-gray-300 px-2 py-1 text-sm text-text-base hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={!canSubmit}
							className="rounded bg-[#008782] px-2 py-1 text-sm font-medium text-white hover:bg-[#004C4B] disabled:opacity-50"
						>
							{saving
								? editing
									? "Saving…"
									: "Adding…"
								: editing
									? "Save changes"
									: "Add row"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default KpiAddMetricDialog;
